import React, { useEffect, useState, ReactNode, useMemo } from "react";
import { QueryInfo } from "system/query/QueryInfo";
import { Box, DialogContent, DialogTitle, Typography,
  CircularProgress, useTheme, Tabs, Tab, Button, DialogActions, Tooltip } from "@mui/material";
import { mergeQueries, socialDatasetId, Query } from "system/query/Query";
import APIRequest, { APIError, enqueueAPIErrorSnackbar } from "system/APIRequest";
import RecordTableContent from "components/widgets/tables/RecordTable/RecordTableContent";
import PostListContent from "components/widgets/lists/posts/PostListContent";
import { getPostListSortOptions } from "components/widgets/lists/posts/PostListUtils";
import SortButton, { DefaultOrderValue } from "routes/tagManagement/SortButton";
import { IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { TextLoader } from "components/apiHandling/TextLoader";
import WidgetAlert from "components/widgetParts/WidgetAlert";
import { QueryDrilldownOverviewContent } from "./QueryDrilldownOverviewContent";
import DashboardSelectionButton from "components/resources/dashboards/DashboardSelectionButton";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import ResponsiveDialog from "../ResponsiveDialog";
import { permissionTest } from "system/InfegyUser";
import { AIResponseType } from "system/AIResponseTypes";
import { AIStandaloneLoader } from "components/widgetParts/AIStandaloneLoader";

export interface QueryDrilldownDialogProps {
    title: string;
    defaultDrilldownDashboardId?: string;
    description?: string;

    drilldownQuery?: Query;
    activeQueryIds?: string[];
    onClose: Function;
    widgetRequestInfo?: any;

    hideDashboardDrilldown?: boolean;

    // UNUSED, legacy prop
    queryInfo?: QueryInfo;
}

function TabPanel(props: {value: number, index: number, children?: ReactNode}): JSX.Element {
  return (
    <div role="tabpanel">
      {props.value === props.index && (
        props.children
      )}
    </div>
  )
}

// CloneProps is a helper function that takes in a props object and returns the children of the props object
// This helps to force MUI Tooltip to render MUI tabs in disabled states
function CloneProps(props: any) {
  const { children, ...other } = props;
  return children(other);
}

export function QueryDrilldownDialog(props: QueryDrilldownDialogProps): React.JSX.Element | null {
  const {i18n} = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const drilldownQuery = props.drilldownQuery;

  const userAIEnabled = permissionTest("ai_features");

  const [drilldownDashboardId, setDrilldownDashboardId] = useState<string | undefined>((props.defaultDrilldownDashboardId) || undefined);
  const [activeQueries, setActiveQueries] = useState<Query[]>([]);

  const [apiResponse, setApiResponse] = useState<unknown>({});
  const [apiError, setApiError] = useState<APIError | undefined>();
  const [apiIsLoading, setApiIsLoading] = useState<boolean>(false);
  const [tabValue, setTabValue] = useState<number>(0);

  // Fetch the current dataset ID from any of the active queries
  const datasetId = drilldownQuery?.dataset_id || activeQueries[0]?.dataset_id || undefined;

  // Reference: Infegy Social Dataset ID
  const isSocialDataset = (datasetId === socialDatasetId);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => setTabValue(newValue);

  const postListSortOptions = useMemo(() => getPostListSortOptions(i18n.t), [i18n.language]);
  const [postListSortField, setPostListSortField] = useState<string>(postListSortOptions[0].value);
  const [postListSortOrder, setPostListSortOrder] = useState<DefaultOrderValue>("desc");
  const handlePostListSortChange = (sortField: string, order: DefaultOrderValue) => {
    setPostListSortField(sortField);
    setPostListSortOrder(order);
  };

  const scrollRef = React.useRef<HTMLDivElement>(null);
  function scrollToTop() {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  }

  // Users that are unable to make queries will be unable to view drilldowns.
  if (!permissionTest("run_query")) {
    // onClose cannot be called on this frame because React is trying to render this component on this frame - it will error.
    setTimeout(() => {
      props.onClose();
      enqueueSnackbar(i18n.t('dashboard.errors.error-user-auth-drilldown'), {variant: 'info', persist: true});
    }, 1);
    return null;
  }

  // Load records from the API
  useEffect(() => {
    const recordsRequestBody = isSocialDataset ?
      { "records": { "size": 100, "highlight": { pre: `<i>`, post: "</i>" }, "sort": [{"field": postListSortField, "direction": postListSortOrder}]}} :
      { "records": { "size": 100, "highlight": { pre: `<i>`, post: "</i>" }}};
    const localRequestBody = {...props.drilldownQuery, ...recordsRequestBody};

    setApiIsLoading(true);
    APIRequest(`query/records/${props.activeQueryIds?.join(",") || ""}`, {}, {
      method: "POST",
      "body": JSON.stringify(localRequestBody)
    }).then((response) => {
      setApiIsLoading(false);
      setApiResponse(response || {});
    }).catch((err: APIError) => {
      enqueueAPIErrorSnackbar(err, enqueueSnackbar);
      setApiIsLoading(false);
      setApiError(err);
    });

  }, [props.drilldownQuery, props.activeQueryIds, postListSortField, postListSortOrder, isSocialDataset]);

  // fetch the full queries from the API based on the activeQueryIds
  useEffect(() => {
    setActiveQueries([]);
    props.activeQueryIds?.forEach((queryId) => {
      return APIRequest(`obj/query/${queryId}`).then((response) => {
        const query = new Query(response.output);
        setActiveQueries((prevQueries) => [...prevQueries, query]);
      }).catch((err: APIError) => {
        enqueueAPIErrorSnackbar(err, enqueueSnackbar);
      });
    });
  }, [props.activeQueryIds]);

  const fullDrilldownQuery = useMemo(() => {
    if (!activeQueries || !activeQueries.length || activeQueries.length !== props.activeQueryIds?.length) {
      return drilldownQuery;
    }

    let mergedQuery = drilldownQuery ? new Query(drilldownQuery) : undefined;
    if (mergedQuery) {
      mergedQuery.dataset_id = activeQueries[0].dataset_id;
    }
    activeQueries.forEach((query) => {
      mergedQuery = mergeQueries([mergedQuery, query], { flatten: true, preserveFirstQueryPrompt: true });
    });
    if (!mergedQuery) {
      return new Query({});
    }
    mergedQuery.name = `Drilldown: ${props.title}`;
    mergedQuery.is_ephemeral = true;
    mergedQuery.is_saved = false;
    delete mergedQuery.id;

    return mergedQuery;
  }, [drilldownQuery, activeQueries]);

  const handleClose = () => {
    props.onClose();
  }

  return (
    <ResponsiveDialog
      open={true}
      onClose={(event) => {
        props.onClose(event);
      }}
      onClick={(event) => {
        event.stopPropagation();
        const className = (event.target as any).className;
        if (className?.startsWith && className.startsWith("MuiDialog-container")) {
          props.onClose(event);
        }
      }}
    >
      <DialogTitle>
        <Typography variant="h4">
          {props.title}
        </Typography>
        { props.description &&
          <Typography variant="body1">
            {props.description}
          </Typography>
        }
        <IconButton edge="end" color="inherit" onClick={handleClose} aria-label="close" sx={{ position: "absolute", right: "20px", top: "10px" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box sx={{ padding: 2, backgroundColor: theme.palette.background.default, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label={i18n.t("drilldowns.tab-overview")}/>
          {isSocialDataset && ( <Tab label={i18n.t("drilldowns.tab-list")}/> )}
          <Tab label={i18n.t("drilldowns.tab-table")}/>
          <CloneProps>
            {(tabProps: any) => (
              <Tooltip title={userAIEnabled ? "" : `${i18n.t("drilldowns.ai-tooltip")}`} followCursor={true}>
                <div>
                  <Tab {...tabProps} label={i18n.t("drilldowns.tab-ai")} disabled={!userAIEnabled}/>
                </div>
              </Tooltip>
            )}
          </CloneProps>
        </Tabs>
      </Box>

      <DialogContent dividers sx={{ height: "100%", width: "100%" }} ref={scrollRef}>
        {apiIsLoading && (
          <Box sx={{ textAlign: "center", marginTop: "20px" }}>
            <CircularProgress />
            <TextLoader />
          </Box>
        )}
        {!apiIsLoading && !!apiResponse && !apiError && (
          <>
            <TabPanel value={tabValue} index={0}>
              <QueryDrilldownOverviewContent
                drilldownQuery={drilldownQuery}
                activeQueryIds={props.activeQueryIds}
                requestInfo={props.widgetRequestInfo}
              />
            </TabPanel>
            {isSocialDataset && (
              <TabPanel value={tabValue} index={1}>
                <Box sx={{ display: "flex", justifyContent: "flex-end", mr: 2 }}>
                  <SortButton
                    initialOrder={postListSortOrder}
                    initialSort={postListSortField}
                    onChange={handlePostListSortChange}
                    sortByOptions={postListSortOptions}
                  />
                </Box>
                <PostListContent posts={apiResponse.records} labels={apiResponse.value_labels} pageSize={10} onScrollToRef={scrollToTop}/>
              </TabPanel>
            )}
            <TabPanel value={tabValue} index={isSocialDataset ? 2 : 1}>
              <RecordTableContent apiResponse={apiResponse} records={apiResponse.records} pageSize={10} />
            </TabPanel>
            <TabPanel value={tabValue} index={isSocialDataset ? 3 : 2}>
            <AIStandaloneLoader
                queryIds={props.activeQueryIds}
                queryBody={drilldownQuery}
                responseType={AIResponseType.SUMMARY} />
            </TabPanel>
          </>
        )}
        {!!apiError && (
          <WidgetAlert severity="warning" alertTitleText="API Error">
            {apiError.message}
          </WidgetAlert>
        )}
      </DialogContent>
      <DialogActions>
        {!props.hideDashboardDrilldown &&
          <>
            <DashboardSelectionButton
              limitToTemplateDashboards={true}
              selectedDashboardId={drilldownDashboardId || ""}
              setSelectedDashboardId={(currentDashboardId:string | undefined) => {
                setDrilldownDashboardId(currentDashboardId);
              }}
            />
            <Button
              disabled={!drilldownDashboardId || !fullDrilldownQuery}
              onClick={() => {
                // Save query
                APIRequest("obj/query", {}, {
                  method: "POST",
                  "body": JSON.stringify(fullDrilldownQuery)
                }).then((response) => {
                  const queryId = response.id;
                  navigate(`/dashboard/${drilldownDashboardId}/${queryId}`);
                }).catch((error: APIError) => {
                  enqueueSnackbar(`${i18n.t('query-form.error-saving-query')}: ${error.message}`, {variant: 'error', persist: true});
                });
              }}
              color="primary"
            >
              {i18n.t("drilldown")}
            </Button>
          </>
        }
        <div style={{flex: '1 0 0'}} />
        <Button onClick={handleClose} color="primary" data-sid="QueryDrilldownDialog-close">
          {i18n.t("close")}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}
